/**
 * Cross-sprint duplicate handler.
 * 
 * Duplicates a task within its own sprint (not the current sprint if different).
 * Also duplicates nested campaigns with full ID remapping so all internal
 * connections, decisions, and flowchart structure are preserved.
 */

import { deepCloneNestedSprintTree } from '../../FlowchartView/hooks/clipboardUtils/directDuplicate';

const LOG_PREFIX = '[useCrossSprintActions]';

/**
 * Deep clones and resets a task for duplication.
 */
const cloneTask = (taskToDuplicate, parentSprint, timestamp, randomSuffix) => {
  const now = new Date().toISOString();
  const newTaskId = `task-${timestamp}-${randomSuffix}`;

  const duplicatedTask = {
    ...JSON.parse(JSON.stringify(taskToDuplicate)),
    id: newTaskId,
    name: `${taskToDuplicate.name} (copy)`,
    order: (parentSprint.tasks?.length || 0) + 1,
    status: 'pending',
    completedAt: null,
    blockedReason: null,
    blockedAt: null,
    createdAt: now,
    updatedAt: now,
    startedAt: null,
    dueDate: null,
    dependsOn: [],
    unlockConditions: [],
    subtasks: (taskToDuplicate.subtasks || []).map((st, idx) => ({
      ...st,
      id: `subtask-${timestamp}-${idx}`,
      completed: false,
    })),
    deliverables: (taskToDuplicate.deliverables || []).map((del, idx) => ({
      ...del,
      id: `deliverable-${timestamp}-${idx}`,
      completed: false,
      link: null,
    })),
    inputs: (taskToDuplicate.inputs || []).map((inp, idx) => ({
      ...inp,
      id: `input-${timestamp}-${idx}`,
    })),
    ...(taskToDuplicate.x !== undefined && { x: taskToDuplicate.x + 40 }),
    ...(taskToDuplicate.y !== undefined && { y: taskToDuplicate.y + 40 }),
  };

  if (duplicatedTask.nodeType === 'decision' && duplicatedTask.options) {
    duplicatedTask.options = duplicatedTask.options.map((opt, idx) => ({
      ...opt,
      id: `option-${timestamp}-${idx}`,
    }));
    duplicatedTask.answer = null;
  }

  return { duplicatedTask, newTaskId, now };
};

/**
 * Creates the duplicate handler.
 */
export const createDuplicateHandler = ({
  findSprintForTask,
  allSprints,
  onAddSprint,
  onUpdateSprint,
}) => (taskId) => {
  const { sprint: parentSprint } = findSprintForTask(taskId);

  if (!parentSprint) {
    console.warn(`${LOG_PREFIX} handleCrossSprintDuplicate: Task not found in any sprint:`, taskId);
    return;
  }

  const taskToDuplicate = parentSprint.tasks?.find(t => t.id === taskId);
  if (!taskToDuplicate) {
    console.warn(`${LOG_PREFIX} handleCrossSprintDuplicate: Task not found in sprint:`, taskId);
    return;
  }

  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 9);
  
  const { duplicatedTask, newTaskId, now } = cloneTask(
    taskToDuplicate,
    parentSprint,
    timestamp,
    randomSuffix
  );

  let clonedNestedRoot = null;
  let clonedNestedDescendants = [];

  if (taskToDuplicate.nestedSprintId) {
    const originalNested = allSprints.find(s => s.id === taskToDuplicate.nestedSprintId);
    if (originalNested) {
      const { sprint: clonedNested, descendants } = deepCloneNestedSprintTree(
        originalNested,
        newTaskId,
        parentSprint.id,
        allSprints.filter(s => s.parentSprintId === parentSprint.id),
        allSprints,
        null,
      );
      if (clonedNested) {
        duplicatedTask.nestedSprintId = clonedNested.id;
        clonedNestedRoot = clonedNested;
        clonedNestedDescendants = descendants || [];
      }
    } else {
      duplicatedTask.nestedSprintId = undefined;
    }
  }

  const updatedSprint = {
    ...parentSprint,
    tasks: [...(parentSprint.tasks || []), duplicatedTask],
  };

  onUpdateSprint?.(updatedSprint);

  if (clonedNestedRoot) {
    onAddSprint?.({ ...clonedNestedRoot, parentSprintId: clonedNestedRoot.parentSprintId ?? parentSprint.id });
    clonedNestedDescendants.forEach((child) => onAddSprint?.(child));
  }

  console.log(`${LOG_PREFIX} Duplicated task:`, {
    originalTaskId: taskId,
    newTaskId,
    sprintId: parentSprint.id,
    hasNestedCampaign: !!taskToDuplicate.nestedSprintId,
  });
};
